import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';

const BreakEvenMeter = ({ centerId, centerName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (centerId) loadData();
  }, [centerId]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Get center financial data
      const { data: center } = await supabase?.from('centers')?.select('fixed_monthly_operating_cost, target_revenue, name')?.eq('id', centerId)?.single();

      // Get total active rent from agreements
      const { data: agreements } = await supabase?.from('agreements')?.select('rent_amount')?.eq('center_id', centerId)?.eq('status', 'active');

      const totalRent = agreements?.reduce((sum, a) => sum + (parseFloat(a?.rent_amount) || 0), 0) || 0;
      const operatingCost = parseFloat(center?.fixed_monthly_operating_cost) || 0;
      const targetRevenue = parseFloat(center?.target_revenue) || 0;

      const healthScore = operatingCost > 0 ? Math.min((totalRent / operatingCost) * 100, 200) : 0;
      const targetScore = targetRevenue > 0 ? Math.min((totalRent / targetRevenue) * 100, 100) : 0;

      setData({
        totalRent,
        operatingCost,
        targetRevenue,
        healthScore,
        targetScore,
        centerName: center?.name || centerName,
      });
    } catch (err) {
      console.error('Break-even load error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-4" />
        <div className="h-3 bg-gray-200 rounded-full" />
      </div>
    );
  }

  if (!data) return null;

  const { totalRent, operatingCost, healthScore, targetScore } = data;
  const isAboveBreakEven = healthScore >= 100;
  const isNearBreakEven = healthScore >= 80 && healthScore < 100;

  const meterColor = isAboveBreakEven
    ? 'bg-green-500'
    : isNearBreakEven
    ? 'bg-yellow-500' :'bg-red-500';

  const statusLabel = isAboveBreakEven
    ? 'Above Break-even'
    : isNearBreakEven
    ? 'Near Break-even' :'Below Break-even';

  const statusColor = isAboveBreakEven
    ? 'text-green-700 bg-green-50 border-green-200'
    : isNearBreakEven
    ? 'text-yellow-700 bg-yellow-50 border-yellow-200' :'text-red-700 bg-red-50 border-red-200';

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
            <Icon name="Activity" size={16} color="white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Break-even Meter</h3>
            <p className="text-xs text-gray-500">{data?.centerName}</p>
          </div>
        </div>
        <span className={`px-2 py-1 rounded-full text-xs font-semibold border ${statusColor}`}>
          {statusLabel}
        </span>
      </div>
      {/* Health Score */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-1.5">
          <span className="text-xs text-gray-500">Health Score (Rent / Operating Cost)</span>
          <span className="text-sm font-bold text-gray-900">{healthScore?.toFixed(1)}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
          <div
            className={`h-3 rounded-full transition-all duration-700 ${meterColor}`}
            style={{ width: `${Math.min(healthScore, 100)}%` }}
          />
        </div>
        <div className="flex justify-between mt-1">
          <span className="text-xs text-gray-400">0%</span>
          <span className="text-xs text-gray-400 font-medium">Break-even: 100%</span>
        </div>
      </div>
      {/* Target Revenue Progress */}
      {data?.targetRevenue > 0 && (
        <div className="mb-4">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-xs text-gray-500">Target Revenue Progress</span>
            <span className="text-sm font-bold text-gray-900">{targetScore?.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div
              className="h-2 rounded-full bg-blue-500 transition-all duration-700"
              style={{ width: `${Math.min(targetScore, 100)}%` }}
            />
          </div>
        </div>
      )}
      {/* Financial Summary */}
      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-100">
        <div>
          <p className="text-xs text-gray-500">Monthly Revenue</p>
          <p className="text-sm font-bold text-gray-900">
            ₹{(totalRent / 100000)?.toFixed(1)}L
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Operating Cost</p>
          <p className="text-sm font-bold text-gray-900">
            ₹{(operatingCost / 100000)?.toFixed(1)}L
          </p>
        </div>
      </div>
    </div>
  );
};

export default BreakEvenMeter;
